// ec-i18n.js — the bilingual layer. Loads locales/ec.en.json + ec.bn.json and
// swaps every user-facing string, instantly, no page reload.
//
// ENGLISH IS THE DEFAULT (Run 4 §1). A first-time visitor with nothing in
// localStorage sees English; the Liquid markup itself ships with correct
// English text baked into every [data-i18n] element, so the page is already
// right even before this file's fetch() resolves. Bengali only replaces it
// once the saved preference or a toggle click asks for it.
//
// ADMIN-EDITABLE BY DESIGN: locales/ec.en.json and ec.bn.json are the single
// source of truth for copy. Re-uploading either file to the theme's Assets is
// the whole edit — no code change, no rebuild. See README "Editing copy /
// translations".
//
// NUMBERS: both locale files use international digits (0-9) throughout, per
// Run 4's rule. The ৳ currency mark stays ৳ in both languages — never "Tk"/"BDT".
//
// FAILS QUIET: if the locale JSON never loads (offline, CDN hiccup), the page
// keeps the English the Liquid already rendered; only the toggle itself stops
// doing anything, and it is the one thing allowed to show a raw key rather
// than crash — see the comment on t() below.

(function (root) {
  'use strict';

  var LOCALES = ['en', 'bn'];
  var DEFAULT_LOCALE = 'en';
  var STORAGE_KEY = 'ec_locale';

  var dicts = { en: null, bn: null };
  var locale = DEFAULT_LOCALE;
  var isReady = false;
  var readyQueue = [];
  var changeListeners = [];

  function $$(sel, scope) {
    return Array.prototype.slice.call((scope || document).querySelectorAll(sel));
  }

  function srcFor(loc) {
    var map = root.EC_I18N_SRC || {};
    return map[loc] || ('../locales/ec.' + loc + '.json');
  }

  function fetchLocale(loc) {
    if (typeof fetch !== 'function') {
      dicts[loc] = dicts[loc] || {};
      return Promise.resolve();
    }
    return fetch(srcFor(loc), { headers: { Accept: 'application/json' } })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.json();
      })
      .then(function (data) {
        dicts[loc] = data && typeof data === 'object' ? data : {};
      })
      .catch(function (e) {
        if (root.console) {
          console.warn('[ec-i18n] failed to load locale "' + loc + '": ' + (e && e.message));
        }
        dicts[loc] = dicts[loc] || {};
      });
  }

  function restoreLocale() {
    try {
      if (!root.localStorage) return;
      var saved = root.localStorage.getItem(STORAGE_KEY);
      if (LOCALES.indexOf(saved) !== -1) locale = saved;
    } catch (e) {
      /* private mode / storage disabled — default stands */
    }
  }

  function persistLocale() {
    try {
      if (root.localStorage) root.localStorage.setItem(STORAGE_KEY, locale);
    } catch (e) {
      /* the choice simply will not survive a reload */
    }
  }

  function format(str, vars) {
    if (!vars) return str;
    return String(str).replace(/\{(\w+)\}/g, function (m, k) {
      return Object.prototype.hasOwnProperty.call(vars, k) ? String(vars[k]) : m;
    });
  }

  /**
   * Look up a string by key in the active locale, falling back to English,
   * falling back to the raw key.
   *
   * That last fallback is a deliberate exception to "never show raw plumbing
   * to a user" — it only fires if BOTH locale JSON files failed to load
   * (fetchLocale already substituted `{}` in that case) AND the key is one
   * used only for JS-generated content with no server-rendered English
   * already in the DOM to fall back on. A visible key beats a blank toast.
   */
  function t(key, vars) {
    var dict = dicts[locale];
    var v = dict && Object.prototype.hasOwnProperty.call(dict, key) ? dict[key] : undefined;
    if (v === undefined) {
      var en = dicts.en;
      v = en && Object.prototype.hasOwnProperty.call(en, key) ? en[key] : undefined;
    }
    if (v === undefined) return key;
    return format(v, vars);
  }

  /**
   * Resolve a bilingual data object — appliance defs ({en, bn, watts, ...}),
   * company.name, category.label — to the string for the active locale.
   * Bengali is the fallback because a handful of Run-3 config entries may
   * still lack an `en` field; never render a blank tile label.
   */
  function pick(obj) {
    if (!obj) return '';
    return obj[locale] || obj.bn || obj.en || '';
  }

  function getLocale() {
    return locale;
  }

  function onChange(fn) {
    if (typeof fn === 'function') changeListeners.push(fn);
    return API;
  }

  function emitChange() {
    changeListeners.forEach(function (fn) {
      try {
        fn(locale);
      } catch (e) {
        if (root.console) console.error('[ec-i18n] onChange listener error', e);
      }
    });
  }

  /** Apply every [data-i18n*] hook in `scope` (default: whole document). */
  function applyStatic(scope) {
    if (typeof document === 'undefined') return;
    var scopeEl = scope || document;

    $$('[data-i18n]', scopeEl).forEach(function (el) {
      var key = el.getAttribute('data-i18n');
      if (!key) return;
      el.textContent = t(key);
    });

    ['aria-label', 'placeholder', 'title'].forEach(function (attr) {
      $$('[data-i18n-' + attr + ']', scopeEl).forEach(function (el) {
        var key = el.getAttribute('data-i18n-' + attr);
        if (key) el.setAttribute(attr, t(key));
      });
    });

    $$('[data-ec-i18n-root]', scopeEl).forEach(function (el) {
      el.setAttribute('lang', locale);
    });

    if (document.documentElement) {
      document.documentElement.setAttribute('data-ec-locale', locale);
    }

    syncToggles();
  }

  function syncToggles() {
    $$('[data-ec-lang-btn]').forEach(function (btn) {
      var loc = btn.getAttribute('data-ec-lang-btn');
      var active = loc === locale;
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      if (btn.parentElement) btn.parentElement.setAttribute('data-active', loc);
    });
  }

  function announceChange() {
    var langKey = locale === 'en' ? 'toggle.languageEnglish' : 'toggle.languageBengali';
    var msg = t('toggle.switchedTo', { language: t(langKey) });
    $$('[data-ec-lang-announce]').forEach(function (el) {
      el.textContent = msg;
    });
  }

  function setLocale(loc) {
    if (LOCALES.indexOf(loc) === -1 || loc === locale) return;
    locale = loc;
    persistLocale();
    applyStatic();
    announceChange();
    emitChange();
  }

  function bindToggles() {
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-ec-lang-btn]') : null;
      if (!btn) return;
      setLocale(btn.getAttribute('data-ec-lang-btn'));
    });
  }

  function whenReady(fn) {
    if (typeof fn !== 'function') return;
    if (isReady) fn();
    else readyQueue.push(fn);
  }

  function boot() {
    restoreLocale();
    if (typeof document !== 'undefined') bindToggles();

    Promise.all(LOCALES.map(fetchLocale)).then(function () {
      isReady = true;
      applyStatic();
      var queue = readyQueue;
      readyQueue = [];
      queue.forEach(function (fn) {
        try {
          fn();
        } catch (e) {
          if (root.console) console.error('[ec-i18n] ready listener error', e);
        }
      });
    });
  }

  var API = {
    t: t,
    pick: pick,
    getLocale: getLocale,
    setLocale: setLocale,
    onChange: onChange,
    applyStatic: applyStatic,
    whenReady: whenReady,
    DEFAULT_LOCALE: DEFAULT_LOCALE,
    LOCALES: LOCALES
  };

  root.EC_I18N = API;

  if (typeof document !== 'undefined') boot();
})(typeof globalThis !== 'undefined' ? globalThis : this);
